
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function useStore() {
const { api, bumppyApi, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const parseApiImages = (imageValue) => {
    try {
      if (!imageValue) return [];
      if (Array.isArray(imageValue)) return imageValue;

      if (typeof imageValue === "string") {
        const parsed = JSON.parse(imageValue);
        return Array.isArray(parsed) ? parsed : [];
      }

      return [];
    } catch {
      return [];
    }
  };

  const fetchStoreItemsApi = async (setItems, setFilteredItems, setLoading) => {
    setLoading?.(true);

    try {
      const res = await api.post("/api/Store/v1.0/getPackkyStoreItems", {
        userId: String(AdminId),
      });

      if (res?.data?.responseCode === "00") {
        const formatted = (res.data?.data || []).map((item) => ({
          ...item,
          imageList: parseApiImages(item.image),
        }));

        setItems?.(formatted);
        setFilteredItems?.(formatted);

        return formatted;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch store items"
        );
      }

      setItems?.([]);
      setFilteredItems?.([]);
      return [];
    } catch (error) {
      console.error("fetchStoreItemsApi error:", error);
      toast.error("Error fetching store items");
      setItems?.([]);
      setFilteredItems?.([]);
      return [];
    } finally {
      setLoading?.(false);
    }
  };

  const fetchStoreCategoriesApi = async (setCategories, setCategoryLoading) => {
    setCategoryLoading?.(true);

    try {
      const res = await api.post("/api/Store/v1.0/getAllItemCategory", {
        categoryId: "",
      });

      if (res?.data?.responseCode === "00") {
        const list = res.data?.data || [];
        setCategories?.(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch categories"
        );
      }

      setCategories?.([]);
      return [];
    } catch (error) {
      console.error("fetchStoreCategoriesApi error:", error);
      toast.error("Error fetching categories");
      setCategories?.([]);
      return [];
    } finally {
      setCategoryLoading?.(false);
    }
  };

  const addStoreCategoryApi = async (categoryName, onSuccess) => {
    try {
      const res = await api.post("/api/Store/v1.0/addPackkyStoreCategory", {
        categoryName,
      });

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Category added successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add category");
      }

      return false;
    } catch (error) {
      console.error("addStoreCategoryApi error:", error);
      toast.error("Error adding category");
      return false;
    }
  };

  const deleteStoreCategoryApi = async (categoryId, onSuccess) => {
    if (!categoryId) {
      toast.error("Invalid category ID");
      return false;
    }

    try {
      const res = await api.post(
        "/api/Store/v1.0/deletePackkyStoreCategory",
        {
          categoryId: String(categoryId),
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Category deleted successfully"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete category");
      }

      return false;
    } catch (error) {
      console.error("deleteStoreCategoryApi error:", error);
      toast.error("Error deleting category");
      return false;
    }
  };

const convertFileToPngBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Image file is missing"));
      return;
    }

    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");

        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const context = canvas.getContext("2d");

        if (!context) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Canvas context could not be created"));
          return;
        }

        // Transparent image ke liye transparent background preserve rahega
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);

        // Actual PNG image generate hogi
        const pngDataUrl = canvas.toDataURL("image/png");

        URL.revokeObjectURL(objectUrl);

        const pngBase64 = pngDataUrl.includes(",")
          ? pngDataUrl.split(",")[1]
          : pngDataUrl;

        resolve(pngBase64);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Selected image could not be processed"));
    };

    image.src = objectUrl;
  });
};

const uploadStoreImageApi = async ({ file, fileName, userId }) => {
  try {
    if (!file) {
      return {
        success: false,
        message: "Image file is missing",
      };
    }

    /*
     * WebP/JPEG/JPG sabko actual PNG base64 me convert karega.
     * PNG base64 generally iVBOR se start hota hai.
     */
    const pngBase64 = await convertFileToPngBase64(file);

    const cleanFileName = String(fileName || file?.name || "store_item")
      .replace(/\.[^/.]+$/, "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "")
      .replace(/_+/g, "_");

    const payload = {
      image_data: pngBase64,
      item_name: cleanFileName,
      path: "store",
      type: "icon",
      userid: String(userId || AdminId || ""),
    };

    if (!payload.userid) {
      return {
        success: false,
        message: "User ID is missing",
      };
    }

    console.log("Upload image payload:", {
      ...payload,
      image_data: `${pngBase64.substring(0, 40)}...`,
      originalFileType: file.type,
      originalFileName: file.name,
    });

    // URL same rahega
    const response = await bumppyApi.post("/v1/uploadImagev2.php", payload);

    const result = response?.data || {};

    console.log("Upload image response:", result);

    if (String(result?.status) !== "0") {
      return {
        success: false,
        message:
          result?.errors?.input?.[0] || result?.msg || "Image upload failed",
        response: result,
      };
    }

    const responseFile =
      result?.image_url ||
      result?.imageUrl ||
      result?.url ||
      result?.file_url ||
      result?.data ||
      "";

    if (!responseFile) {
      return {
        success: false,
        message: "Image filename or URL not received",
        response: result,
      };
    }

    const responseFileString = String(responseFile);

    const imageUrl =
      responseFileString.startsWith("http://") ||
      responseFileString.startsWith("https://")
        ? responseFileString
        : `https://www.packky.com/images/${encodeURIComponent(
            responseFileString,
          )}`;

    return {
      success: true,
      imageUrl,
      imageName: responseFileString,
      response: result,
    };
  } catch (error) {
    console.error("uploadStoreImageApi error:", error?.response?.data || error);

    return {
      success: false,
      message:
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        "Image upload request failed",
    };
  }
};

const saveStoreItemApi = async ({ itemData, onSuccess, onFinally }) => {
  try {
    const itemName = String(itemData?.itemName || "").trim();

    if (!itemName) {
      toast.error("Item name is required");
      return false;
    }

    const selectedImages = Array.isArray(itemData?.image) ? itemData.image : [];

    if (!selectedImages.length) {
      toast.error("Please select at least one image");
      return false;
    }

    const preparedImageUrls = [];

  for (let index = 0; index < selectedImages.length; index++) {
    const image = selectedImages[index];

    // Existing URL ko dobara upload nahi karna
    if (
      typeof image === "string" &&
      (image.startsWith("http://") || image.startsWith("https://"))
    ) {
      preparedImageUrls.push(image);
      continue;
    }

    const existingImageUrl =
      image?.url || image?.imageUrl || image?.image_url || "";

    if (
      existingImageUrl.startsWith("http://") ||
      existingImageUrl.startsWith("https://")
    ) {
      preparedImageUrls.push(existingImageUrl);
      continue;
    }

    const selectedFile = image?.file;

    if (!selectedFile) {
      toast.error(`Image ${index + 1} file is missing`);
      return false;
    }

    const uploadResponse = await uploadStoreImageApi({
      file: selectedFile,
      fileName: `${itemName}_${index + 1}`,
      userId: AdminId,
    });

    if (!uploadResponse?.success) {
      toast.error(
        uploadResponse?.message || `Image ${index + 1} upload failed`,
      );

      return false;
    }

    preparedImageUrls.push(uploadResponse.imageUrl);
  }

    if (!preparedImageUrls.length) {
      toast.error("Image upload failed");
      return false;
    }

    const payload = {
      items: [
        {
          ...(itemData?.itemId
            ? {
                itemId: String(itemData.itemId),
              }
            : {}),

          itemName,
          category: String(itemData?.category || ""),
          size: String(itemData?.size || ""),
          description: String(itemData?.description || "").trim(),
          isReturnable: itemData?.isReturnable === "No" ? "No" : "Yes",
          price: String(itemData?.price ?? ""),

          // Save API me only uploaded complete URLs jayengi
          image: preparedImageUrls,

          color: String(itemData?.color || "").trim(),
        },
      ],
    };

    console.log("Insert/Update Store payload:", payload);

    const response = await api.post(
      "/api/Store/v1.0/insertPackkyStoreItems",
      payload,
    );

    if (response?.data?.responseCode === "00") {
      toast.success(
        itemData?.itemId
          ? "Store item updated successfully"
          : "Store item added successfully",
      );

      await onSuccess?.(response.data);

      return true;
    }

    toast.error(response?.data?.responseMessage || "Failed to save store item");

    return false;
  } catch (error) {
    console.error("saveStoreItemApi error:", error?.response?.data || error);

    toast.error(
      error?.response?.data?.responseMessage ||
        error?.message ||
        "Error saving store item",
    );

    return false;
  } finally {
    onFinally?.();
  }
};

   return {
    fetchStoreItemsApi,
    fetchStoreCategoriesApi,
    addStoreCategoryApi,
    deleteStoreCategoryApi,
    uploadStoreImageApi,
    saveStoreItemApi,
  };
}